<template>
  <div>
    <CreateModal
      :confirmToClose="appointmentCreationConfirmToClose"
      :customCallButton="customCallButton"
      type="appointment"
      ref="createModal"
    >
      <div :class="{ 'appointmentForm__rail': isCalendarStep }">
      <v-form class="mw-600 mx-auto" ref="appointmentForm">
        <v-stepper v-model="appointmentFormStepsCurr" ref="appointmentFormStepsWrapper" class="elevation-0">
          <v-stepper-items ref="appointmentFormSteps">
            <!-- service and branch select -->
            <v-stepper-content class="pa-0" step="1">
              <div :class="$vuetify.breakpoint.smAndUp ? 'pa-5' : 'pa-0'">
                <h2 class="mw-350 mb-5">
                  {{
                    !hasMultipleBranches
                      ? $t('appointment.formCreate.titles.service')
                      : $t('appointment.formCreate.titles.serviceAndSubs')
                  }}
                </h2>

                <CustomerSelect
                  v-if="isServiceProviderUser"
                  :value="appointmentFields.real_estate_id"
                  ref="customerSelect"
                  :label="$t('appointment.formCreate.labels.realEstate')"
                  :customers="serviceProviderCustomers"
                />

                <AppointmentCreationTypeSelect
                  v-if="hideUntilHasRealEstate"
                  :key="`appointment-type-${appointmentFields.real_estate_id}`"
                  ref="typeSelect"
                  :rules="[rules.required]"
                  :label="$t('appointment.formCreate.labels.service')"
                  @resetForm="handleResetForm"
                />

                <ServiceProviderUserSelect
                  v-if="isServiceProviderUser && hideUntilHasRealEstate"
                  ref="serviceProviderUserSelect"
                  v-model="serviceProviderUserId"
                  :visitType="appointmentFields.type"
                  :disabled="!appointmentFields.type"
                  :rules="[]"
                  :label="$t('appointment.formCreate.labels.serviceProviderUser')"
                />

                <BranchSelect
                  v-if="hideUntilHasRealEstate"
                  :key="`appointment-branch-${appointmentFields.real_estate_id}`"
                  ref="branchSelect"
                  v-show="hasMultipleBranches"
                  :rules="[rules.required]"
                  :label="$t('appointment.formCreate.labels.branch')"
                  :value="appointmentFields.real_estate_branch_id"
                  :hasMultipleBranches="hasMultipleBranches"
                  :branches="branches"
                />

                <WorkerTypeOptions
                  v-show="showWorkerTypeField"
                  :key="appointmentFields.type"
                />
              </div>
            </v-stepper-content>
            <!-- /service and branch select -->

            <!-- date select -->
            <v-stepper-content class="pa-0 mb-8" step="2">
              <div :class="$vuetify.breakpoint.smAndUp ? 'pa-5' : 'pa-0'">
                <h2 class="mw-350 mb-5">
                  {{ $t('appointment.formCreate.titles.dateTime') }}
                </h2>

                <HDateTimePicker
                  v-model="dateTime"
                  @setSlotSize="setSlotSize"
                  :rules="isByHours ? [rules.dateTime] : [rules.dateTimePeriod]"
                  :dateLabel="$t('appointment.formCreate.labels.date')"
                  :timeLabel="$t('appointment.formCreate.labels.time')"
                  :periodLabel="$t('appointment.formCreate.labels.period')"
                  :isByHours="isByHours"
                  :requiresEndTime="requiresEndTime"
                  ref="dateTimePicker"
                />

                <!-- ENTRA QUANDO TIVER NO BACK -->
                <!-- <v-divider class="mb-6" /> -->
                <!-- <HSlotSize v-model="appointmentFields.slot_size" isAppointment /> -->
              </div>
            </v-stepper-content>
            <!-- /date select -->

            <!-- real estate search or create -->
            <v-stepper-content class="pa-0" step="3">
              <div :class="$vuetify.breakpoint.smAndUp ? 'pa-5' : 'pa-0'">
                <h2 class="mw-350 mb-5">
                  {{ $t('appointment.formCreate.titles.realtyTitle') }}
                </h2>

                <RealtySearch
                  ref="realtySearch"
                  :rules="[rules.required]"
                  :single="appointmentHasUniqueRealtyServiceType"
                  :apply-new-realty="applyNewRealty"
                  @openRealtyCreateModal="openRealtyCreateModal"
                />

                <BaseReportSelect ref="baseReportSelect" />
              </div>
            </v-stepper-content>
            <!-- /real estate search or create -->

            <!-- meeting location -->
            <v-stepper-content class="pa-0" step="4">
              <div :class="$vuetify.breakpoint.smAndUp ? 'pa-5' : 'pa-0'">
                <h2 v-if="!appointmentHasMeetingLocation" class="mw-300 mb-5">
                  {{ $t('appointment.formCreate.titles.keyLocation') }}
                </h2>
                <h2 v-else class="mw-350 mb-5">
                  {{
                    $t('appointment.formCreate.titles.keyLocationAndMeeting')
                  }}
                </h2>

                <KeyAddress :rules="[rules.required]" />
                <MeetingAddress
                  v-show="appointmentHasMeetingLocation"
                  :rules="[rules.required]"
                />
              </div>
            </v-stepper-content>
            <!-- /meeting location -->

            <!-- visitor data -->
            <v-stepper-content class="pa-0" step="5">
              <div :class="$vuetify.breakpoint.smAndUp ? 'pa-5' : 'pa-0'">
                <h2 class="mw-350 mb-5">
                  {{
                    isVisit
                      ? $t('appointment.formCreate.titles.visitor')
                      : $t('appointment.formCreate.titles.tenant')
                  }}
                </h2>

                <v-row>
                  <v-col cols="12">
                    <VisitorName
                      :rules="isNameRequired ? [rules.name] : [rules.nameCheckPassEmpty]"
                    />
                  </v-col>
                  <v-col cols="12">
                    <VisitorEmail
                      :rules="isEmailRequired ? [rules.email] : [rules.emailCheckPassEmpty]"
                    />
                  </v-col>
                  <v-col cols="12">
                    <HPhone
                      :is-appointment="true"
                      :rules="isPhoneRequired ? [rules.phone] : [rules.phoneCheckPassEmpty]"
                      :country="country"
                      ref="visitorPhone"
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" sm="7">
                    <HDocument
                      :label="$t('forms.cpf')"
                      :is-appointment="true"
                      :rules="isDocumentRequired ? [rules.documentCheck] : [rules.documentCheckPassEmpty]"
                      :country="country"
                      ref="visitorDocument"
                    />
                  </v-col>
                  <v-col cols="12" sm="5">
                    <VisitorBirthDate
                      :rules="
                        isRentOut || isRequiredTenant
                          ? [rules.birthDateCheck]
                          : [rules.birthDateCheckPassEmpty]
                      "
                    />
                  </v-col>
                </v-row>
              </div>
            </v-stepper-content>
            <!-- /visitor data -->

            <!-- term -->
            <v-stepper-content v-if="isRentOut" class="pa-0" step="6">
              <div :class="$vuetify.breakpoint.smAndUp ? 'pa-5' : 'pa-0'">
                <h2 class="mw-350 mb-5">
                  {{
                    $t('appointment.formCreate.titles.terminationFileUpload')
                  }}
                </h2>

                <AppointmentTermFile ref="termUploader" />
              </div>
            </v-stepper-content>
            <!-- /term -->

            <!-- observations, displacement and fci  -->
            <v-stepper-content
              class="pa-0"
              ref="meetingDetails"
              :step="meetingDetailsStep"
            >
              <div :class="$vuetify.breakpoint.smAndUp ? 'pa-5' : 'pa-0'">
                <h2 class="mw-350 mt-5 mb-5">
                  {{ $t('appointment.formCreate.titles.observation') }}
                </h2>

                <MeetingObservations />

                <v-row>
                  <v-col cols="12" :sm="isRentOut ? 6 : 5">
                    <MeetingAmount ref="meetingAmount" />
                  </v-col>
                  <v-col v-if="isRentOut" cols="12" :sm="isRentOut && 6">
                    <FciAmount ref="fciAmount" />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <MeetingAuth />
                  </v-col>
                </v-row>

                <!-- upload attachments -->
                <UploadAttachment v-if="requiresAttachmentUpload" ref="attachmentUploader" />
                <!-- /upload attachments -->

                <v-card v-if="isRentOut" class="px-5 pt-4 pb-5" outlined>
                  <v-checkbox
                    v-model="rentOutConfirmation"
                    :label="$t('rentOut.confirmation')"
                    class="d-flex align-start"
                    hide-details
                  />
                </v-card>

                <AppointmentCustomFields />
              </div>
            </v-stepper-content>
            <!-- /observations, displacement and fci  -->

            <!-- success message -->
            <v-stepper-content class="pa-0 mb-8" step="10">
              <div :class="$vuetify.breakpoint.smAndUp ? 'pa-5' : 'pa-0'">
                <div class="appointmentForm__success mw-400 mx-auto">
                  <v-icon color="green">mdi-check-circle-outline</v-icon>
                  <h2 class="secondary--text">
                    {{ $t('appointment.formCreate.titles.appointmentSuccess') }}
                  </h2>

                  <p class="mb-10">
                    {{ $t('appointment.formCreate.labels.uploadText') }}
                  </p>

                  <AppointmentFilesUpload
                    :selectable="false"
                    componentId="appointmentFilesUpload"
                    ref="appointmentFilesUpload"
                    small
                  />

                  <v-row align="center" class="my-8">
                    <v-col cols="5">
                      <v-divider />
                    </v-col>
                    <v-col cols="2">
                      <span class="body-2 grey--text">
                        {{ $t('upload.or') }}
                      </span>
                    </v-col>
                    <v-col cols="5">
                      <v-divider />
                    </v-col>
                  </v-row>

                  <v-btn
                    @click="handleResetForm"
                    color="primary"
                    elevation="0"
                    large
                    text
                  >
                    {{ $t('appointment.formCreate.actions.createNew') }}
                  </v-btn>
                </div>
              </div>
            </v-stepper-content>
            <!-- /success message -->
          </v-stepper-items>

          <!-- actions (previous / next / create) -->
          <AppointmentCreateActions
            @validate="validate"
            @handleStepValidations="handleStepValidations"
            @handleStepsNavBackward="handleStepsNavBackward"
            :appointmentFormStepsCurr="appointmentFormStepsCurr"
            :appointmentFormStepsTotal="appointmentFormStepsTotal"
            :disabled="isSaveDisabled"
            :isDraftLoading="isDraftLoading"
            :isLoading="isLoading"
          />
          <!-- /actions -->

          <!-- stepper dots -->
          <v-stepper-header
            class="appointmentForm__steps mw-200 mx-auto mt-5 elevation-0"
            v-if="appointmentFormStepsCurr <= 9"
          >
            <v-stepper-step
              v-for="(step, index) in appointmentFormStepsTotal"
              :step="index + 1"
              :key="index"
            />
          </v-stepper-header>
          <!-- /stepper dots -->
        </v-stepper>
      </v-form>
      </div>

      <AppointmentCalendarPanel v-if="isCalendarStep" />

      <AppointmentSummary
        v-if="appointmentFormStepsCurr <= 9 && !isCalendarStep"
        :hasMeetingLocation="appointmentHasMeetingLocation"
        :isVisit="isVisit"
      />
    </CreateModal>
  </div>
</template>

<script>
import CreateModal from '@/components/app/CreateModal'

import HPhone from '../../components/app/HPhone'
import HDocument from '../../components/app/HDocumentID'
import HSlotSize from '../../components/app/HSlotSize'

import HDateTimePicker from '../lib/HDateTimePicker'
import HDatePicker from '../lib/HDatePicker'
import HTimePicker from '../lib/HTimePicker'
import ExternalCode from './formFieldsCreate/appointmentDetails/ExternalCode'
import AppointmentCreationTypeSelect from './formFieldsCreate/appointmentDetails/AppointmentCreationTypeSelect'
import CustomerSelect from './formFieldsCreate/appointmentDetails/CustomerSelect.vue'
import BranchSelect from './formFieldsCreate/appointmentDetails/BranchSelect'
import RealtySearch from './formFieldsCreate/appointmentDetails/RealtySearch'
import BaseReportSelect from './formFieldsCreate/appointmentDetails/BaseReportSelect'
import WorkerTypeOptions from './formFieldsCreate/appointmentDetails/WorkerTypeOptions'

import MeetingLocation from './formFieldsCreate/appointmentMeeting/MeetingLocation'
import MeetingAddress from './formFieldsCreate/appointmentMeeting/MeetingAddress'
import MeetingObservations from './formFieldsCreate/appointmentMeeting/MeetingObservations'
import MeetingAuth from './formFieldsCreate/appointmentMeeting/MeetingAuth'
import MeetingAmount from './formFieldsCreate/appointmentMeeting/MeetingAmount'
import FciAmount from './formFieldsCreate/appointmentMeeting/FciAmount'

import KeyPicker from './formFieldsCreate/keyLocation/KeyPicker'
import KeyLocation from './formFieldsCreate/keyLocation/KeyLocation'
import KeyAddress from './formFieldsCreate/keyLocation/KeyAddress'

import VisitorName from './formFieldsCreate/visitorData/VisitorName'
import VisitorEmail from './formFieldsCreate/visitorData/VisitorEmail'
import VisitorPhone from './formFieldsCreate/visitorData/VisitorPhone'
import VisitorDocument from './formFieldsCreate/visitorData/VisitorDocument'
import VisitorBirthDate from './formFieldsCreate/visitorData/VisitorBirthDate'

import AppointmentFilesUpload from './AppointmentFilesUpload'

import AppointmentSummary from './AppointmentSummary'
import AppointmentCalendarPanel from './AppointmentCalendarPanel'

import RealtyCreateUpdate from '@/components/realty/RealtyCreateUpdate'

import AppointmentCreateActions from './formFieldsCreate/AppointmentCreateActions'
import AppointmentTermFile from './formFieldsCreate/AppointmentTermFile'
import AppointmentCustomFields from './formFieldsCreate/AppointmentCustomFields'

import ServiceProviderUserSelect from '@/views/serviceProvider/fragments/ServiceProviderUserSelect.vue'

import UploadAttachment from './formFieldsCreate/appointmentDetails/UploadAttachment'

import {
  name,
  phone,
  email,
  required,
  dateTime,
  dateTimePeriod,
  documentCheck,
  birthDateCheck,
  nameCheckPassEmpty,
  emailCheckPassEmpty,
  phoneCheckPassEmpty,
  documentCheckPassEmpty,
  birthDateCheckPassEmpty,
} from '@/util/errorHandling'

import { cleanObject } from '@/util/helpers'

import {
  visitTypeServices,
  rentOutTypes,
  requiredTenantTypes,
  optionalTenantTypes,
  isTenantRequiredForType,
  isAttachmentRequiredForType,
  appointmentFormSteps,
  appointmentLocations,
  visibleAddressCases,
} from './formFieldsCreate/appointmentFormDefaults'

import {
  validateDateTime,
  validateRealties,
  validateVisitorInfo,
  validateKeyLocation,
  validateServiceDetails,
  validateMeetingLocation,
} from './formFieldsCreate/appointmentStepsValidation'

import { mapGetters, mapMutations, mapActions } from 'vuex'

export default {
  name: 'AppointmentCreate',

  components: {
    CreateModal,
    HDateTimePicker,
    HDatePicker,
    HTimePicker,
    HDocument,
    HPhone,
    HSlotSize,
    ExternalCode,
    AppointmentCreationTypeSelect,
    BranchSelect,
    RealtySearch,
    BaseReportSelect,
    MeetingLocation,
    MeetingAddress,
    MeetingObservations,
    MeetingAuth,
    MeetingAmount,
    KeyPicker,
    KeyLocation,
    KeyAddress,
    VisitorName,
    VisitorEmail,
    VisitorPhone,
    VisitorDocument,
    VisitorBirthDate,
    AppointmentSummary,
    AppointmentCalendarPanel,
    RealtyCreateUpdate,
    AppointmentFilesUpload,
    FciAmount,
    AppointmentCreateActions,
    AppointmentTermFile,
    AppointmentCustomFields,
    CustomerSelect,
    WorkerTypeOptions,
    ServiceProviderUserSelect,
    UploadAttachment,
  },

  props: {
    customCallButton: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      appointmentFormStepsNextIsActive: true,
      rentOutConfirmation: false,
      meetingDetailsStep: 5,

      appointmentFormSteps,
      appointmentLocations,
      visibleAddressCases,

      valid: true,
      isValid: true,
      isLoading: false,
      isDraftLoading: false,

      rules: {
        name,
        email,
        phone,
        required,
        dateTime,
        dateTimePeriod,
        documentCheck,
        birthDateCheck,
        nameCheckPassEmpty,
        phoneCheckPassEmpty,
        emailCheckPassEmpty,
        documentCheckPassEmpty,
        birthDateCheckPassEmpty,
      },
      dateTime: '',
      files: [],
      applyNewRealty: false,
    }
  },

  computed: {
    ...mapGetters([
      'appointmentHasMeetingLocation',
      'appointmentFormStepsTotal',
      'appointmentFormStepsCurr',
      'appointmentAddresses',
      'appointmentFields',
      'realties',
      'branches',
      'user',
      'appointmentCreationConfirmToClose',
      'appointmentHasUniqueRealtyServiceType',
      'baseReportRequired',
      'hasTerm',
      'hasAttachment',
      'customer',
      'serviceProviderCustomers',
      'inspectors',
      'currentRealEstate',
      'realEstateAvailableServiceTypes',
    ]),

    updateSteps() {
      return this.appointmentFormStepsTotal
    },

    // calendar only shows on the date (step 2) and realty (step 3) selection
    isCalendarStep() {
      return [2, 3].includes(this.appointmentFormStepsCurr)
    },

    serviceProviderUserId: {
      get() {
        return this.appointmentFields?.service_provider_user_id || null
      },
      set(value) {
        this.setAppointmentFields({ service_provider_user_id: value || null })
      },
    },

    hasMultipleBranches() {
      return this.branches && this.branches.length > 1
    },

    isVisit() {
      return visitTypeServices.includes(this.appointmentFields?.type)
    },

    isRentOut() {
      return rentOutTypes.includes(this.appointmentFields?.type)
    },

    requiresAttachmentUpload() {
      return isAttachmentRequiredForType(this.appointmentFields?.type)
    },

    isSaveDisabled() {
      if (this.isRentOut && !this.rentOutConfirmation) return true
      if (this.requiresAttachmentUpload && !this.hasAttachment) return true
      return false
    },

    isRequiredTenant() {
      return requiredTenantTypes.includes(this.appointmentFields?.type)
    },

    isTermination() {
      return this.appointmentFields.type === 'TERMINATION'
    },

    isRentOutOrTermination() {
      return this.isRentOut || this.isTermination
    },

    isTenantFieldsOptional() {
      return optionalTenantTypes.includes(this.appointmentFields?.type)
    },

    isTenantDataRequired() {
      return isTenantRequiredForType(this.appointmentFields?.type)
    },

    country() {
      return this.user?.country ?? localStorage.getItem('user_country')
    },

    isServiceProviderUser() {
      const userRole = localStorage.getItem('access_roles')

      if (!userRole) return false
      return userRole.includes('ROLE_SERVICE_PROVIDER')
    },

    showWorkerTypeField() {
      if (!this.currentRealEstate) return false
      if (!this.currentRealEstate.worker_types) return false
      if (this.currentRealEstate.worker_types.length === 1 && this.currentRealEstate.worker_types.includes('INTERNAL')) return true
      return this.currentRealEstate?.worker_types?.includes('HOST') && this.currentRealEstate?.worker_types?.includes('INTERNAL')
    },

    useCustomWorkerType() {
      return this.currentRealEstate.use_custom_worker_type
    },

    hideUntilHasRealEstate() {
      return !this.isServiceProviderUser || this.appointmentFields.real_estate_id
    },

    isUserLoggedIn() {
      const token = localStorage.getItem('access_token')
      return !!token
    },

    isByHours() {
      // validação por duração exige o fluxo por duração: cair no seletor só de hora deixaria
      // a imobiliária sem como informar o horário final que o backend passou a exigir
      if (this.requiresEndTime) return false
      return !this.currentRealEstate.allows_period_appointment
    },

    // imobiliária que valida por duração precisa do horário final para o backend conseguir
    // comparar sobreposição. Tipos de agenda flexível ficam de fora: a sobreposição não os
    // avalia, e exigir duração faria a vistoria anunciar ao host uma janela que ninguém escolheu.
    requiresEndTime() {
      if (this.currentRealEstate.service_duration_validation_type !== 'STRICT_DURATION') return false

      const selectedType = this.realEstateAvailableServiceTypes.find(
        (serviceType) => serviceType.value === this.appointmentFields.type
      )

      return !!selectedType && !selectedType.flexible_schedule
    },

    isBrazil() {
      return this.country === 'BR'
    },

    isAnyVisitorFieldFilled() {
      const { visitor_name, visitor_mail, visitor_phone, visitor_document } = this.appointmentFields
      return !!(visitor_name || visitor_mail || visitor_phone || visitor_document)
    },

    isNameRequired() {
      if (this.isTenantFieldsOptional) return false
      return this.isTenantDataRequired || (this.isBrazil && this.isAnyVisitorFieldFilled)
    },

    isEmailRequired() {
      if (this.isTenantFieldsOptional || !this.isVisit) return false
      return this.isTenantDataRequired
    },

    isPhoneRequired() {
      if (this.isTenantFieldsOptional) return false
      return this.isTenantDataRequired || (this.isBrazil && this.isAnyVisitorFieldFilled)
    },

    isDocumentRequired() {
      if (this.isTenantFieldsOptional) return false
      return this.isRentOut || this.isRequiredTenant || (this.isBrazil && this.isAnyVisitorFieldFilled)
    },
  },

  watch: {
    dateTime(currentValue) {

      // se mudou a data e ela não tiver horários, devemos jogar o slot_size para 0
      // forçando assim que o usuário escolha um horário
      if (currentValue && currentValue.length <= 11) {
        this.setAppointmentFields({ slot_size: 0 })
        this.setAppointmentFields({ period_key: null })
      }

      this.setAppointmentFields({ date: currentValue })

      this.$nextTick(() => {
        if (currentValue && currentValue.length <= 11) {
          this.$refs.appointmentForm.resetErrorBag()
          this.$refs.appointmentForm.resetValidation()
        } else {
          this.checkRealtiesToPreSelect()
        }

        this.updateRealtyList()
      })
    },

    appointmentFormStepsCurr(currentValue) {
      currentValue === this.appointmentFormStepsTotal &&
        this.getAppointmentAmount()
      this.handleMeetingDetailsStep()

      // fill HPhone
      if (currentValue === 5) {
        this.$nextTick(() => {
          this.$refs.visitorPhone.setInitialPhoneNumber()
        })
      }
    },
  },

  methods: {
    ...mapActions([
      'newAppointment',
      'showErrorMessage',
      'getAppointmentAmount',
      'resetAppointmentFields',
      'fetchUser',
      'fetchRealties',
      'fetchAppointments',
      'fetchBoard',
      'fetchCurrentRealEstate',
    ]),

    ...mapMutations([
      'setAppointmentFormStepsTotal',
      'setAppointmentFormStepsCurr',
      'setAppointmentFields',
      'setAppointmentAmount',
      'setAppointmentCreationConfirmToClose',
      'setAppointmentId',
      'setHasTerm',
      'setHasAttachment',
      'setIsAttachmentUploadSuccessful',
    ]),

    async validate(isDraft = false) {
      if (isDraft) this.isDraftLoading = true
      else this.isLoading = true

      const appointment = this.appointmentFields

      // visitor phone
      if (appointment.visitor_phone) {
        const parsedPhone = appointment.visitor_phone
        appointment.visitor_phone =
          parsedPhone?.length >= 6 ? parsedPhone : null
      }

      // displacement
      if (appointment.displacement) {
        const parsedDisplacement = appointment.displacement
          ? appointment.displacement
          : '0.00'
        appointment.displacement = parsedDisplacement
      }

      if (isDraft) appointment.appointment_request_type = 'DRAFT'
      else appointment.appointment_request_type = null

      const parsedAppointment = cleanObject(appointment)

      if (!this.user.id) await this.fetchUser()

      this.newAppointment(parsedAppointment)
        .then(({ data }) => {
          if (this.$router.currentRoute.path == '/board') {
            this.fetchBoard()
          }

          if (this.$router.currentRoute.path == '/appointments') {
            this.fetchAppointments()
          }

          this.setAppointmentCreationConfirmToClose(false)
          this.setAppointmentFormStepsCurr(10)
          this.setAppointmentId(data?.id)

          const spUserId = parsedAppointment?.service_provider_user_id
          if (spUserId) {
            this.$router.push({
              path: '/appointments',
              query: { serviceProviderUserId: spUserId },
            })
          }
        })
        .catch(({ data }) => {
          data?.errors && data?.errors?.length >= 1
            ? this.showErrorMessage(data.errors[0].message)
            : this.showErrorMessage(data.message)

          // The picked base report may have been taken by another appointment meanwhile
          if (this.baseReportRequired) this.$refs.baseReportSelect?.reload()
        })
        .finally(() => {
          this.isLoading = false
          this.isDraftLoading = false
        })
    },

    handleResetForm() {
      this.dateTime = null
      const form = this.$refs.appointmentForm
      const selectedRealEstateId = this.appointmentFields.real_estate_id
      if (!selectedRealEstateId) form.reset()

      this.setHasTerm(false)
      this.setHasAttachment(false)
      this.setIsAttachmentUploadSuccessful(false)
      this.resetAppointmentFields()
      this.applyNewRealty = false
      this.rentOutConfirmation = false
      this.$refs.typeSelect?.resetValue()
      this.$refs.realtySearch?.resetValue()
      this.$refs.dateTimePicker?.resetValue()
      this.$refs.meetingAmount?.resetValue()
      this.$refs.fciAmount?.resetValue()
      this.$refs.serviceProviderUserSelect?.resetValue()
      this.$refs.attachmentUploader?.resetUploadField()
      this.setAppointmentFields({ service_provider_user_id: null })
      this.setAppointmentCreationConfirmToClose(false)
    },

    handleStepsNavBackward() {
      this.setAppointmentFormStepsCurr(this.appointmentFormStepsCurr - 1)
      this.appointmentFormStepsNextIsActive = true
    },

    handleStepsNavForward() {
      this.$refs.appointmentForm.resetValidation()
      this.setAppointmentFormStepsCurr(this.appointmentFormStepsCurr + 1)
      this.appointmentFormStepsNextIsActive = false
    },

    handleStepValidations() {
      const current = this.appointmentFormStepsCurr
      const validations = this.buildValidations()

      this.$refs.appointmentForm.validate()
      validations[current - 1]()
    },

    handleMeetingDetailsStep() {
      this.meetingDetailsStep = this.isRentOut ? 7 : 6
    },

    buildValidations() {
      return [
        this.handleValidateServiceDetails,
        this.handleValidateDateTime,
        this.handleValidateRealties,
        this.handleValidateKeyLocation,
        this.handleValidateVisitorInfo,
        this.handleTerm,
      ]
    },

    handleValidateServiceDetails() {
      if (validateServiceDetails(this.appointmentFields)) this.handleStepsNavForward()
    },

    handleValidateDateTime() {
      if (this.requiresEndTime && !this.appointmentFields.slot_size) return
      if (validateDateTime(this.appointmentFields)) this.handleStepsNavForward()
    },

    handleValidateRealties() {
      if (validateRealties(this.appointmentFields, this.baseReportRequired)) this.handleStepsNavForward()
    },

    handleValidateMeetingLocation() {
      if (
        validateMeetingLocation(
          this.appointmentFields,
          this.appointmentHasMeetingLocation
        )
      ) {
        this.handleStepsNavForward()
      }
    },

    handleValidateKeyLocation() {
      if (validateKeyLocation(this.appointmentFields))
        this.handleStepsNavForward()
    },

    handleValidateVisitorInfo() {
      if (validateVisitorInfo(this.appointmentFields))
        this.handleStepsNavForward()
    },

    handleTerm() {
      if (this.isRentOut) this.hasTerm && this.handleStepsNavForward()
      else this.handleStepsNavForward()
    },

    updateRealtyList() {
      const realtyComp = this.$refs.realtySearch
      const currentStep = this.appointmentFormStepsCurr

      if (realtyComp?.realties?.length && currentStep === 2) {
        for (const realty of realtyComp.realties) {
          realtyComp.updateSelectedItem(realty)
        }
      }
    },

    checkRealtiesToPreSelect() {
      Promise.resolve(this.fetchRealties())
        .then(() => {
          if (!this.user.id) this.fetchUser()
        })
        .then(() => {
          const onboardingStatus = this.user?.real_estate?.onboarding_status

          if (this.realties.length === 1 && onboardingStatus === 'NO_SERVICE') {
            const id = this.realties[0].id
            this.$refs?.realtySearch?.selectItem({ id })
          }
        })
    },

    openRealtyCreateModal() {
      const realtyCreateModal = this.$parent.$parent?.$refs?.createUpdateRealtyModal
      realtyCreateModal?.openRealtyCreateModal(true)
    },

    closeModal() {
      this.applyNewRealty = false
      this.$refs.createModal.dialog = false
    },

    setSlotSize(slotSize, periodKey) {
      this.setAppointmentFields({ slot_size: slotSize })
      this.setAppointmentFields({ period_key: periodKey })
    },
  },

  mounted() {
    if (!this.isUserLoggedIn) return

    // focus on select type
    this.$watch(
      () => this.$refs.createModal && this.$refs.createModal.dialog,
      async (isOpen) => {
        if (isOpen) {
          await this.fetchCurrentRealEstate()

          this.$nextTick(() => {
            this.$refs.appointmentForm.resetValidation()
            this.$refs.typeSelect?.$children[0].$refs.input.focus()
          })
        }
      }
    )
  },
}
</script>
